/**
 * UTM parameter capture and storage.
 *
 * When a visitor arrives from a campaign link (Product Hunt, X/Twitter,
 * LinkedIn, etc.) the browser URL contains utm_* query parameters.  This hook
 * reads them on first render and writes them to localStorage so they are
 * available throughout the session and for any downstream attribution logic.
 *
 * Campaign URLs that should be shared externally:
 *
 *   Product Hunt listing post:
 *     https://marketmindofficial.com/?utm_source=producthunt&utm_medium=referral&utm_campaign=launch
 *
 *   X / Twitter post:
 *     https://marketmindofficial.com/?utm_source=twitter&utm_medium=social&utm_campaign=launch
 *
 *   LinkedIn post:
 *     https://marketmindofficial.com/?utm_source=linkedin&utm_medium=social&utm_campaign=launch
 */

import { useEffect } from 'react';

export const UTM_STORAGE_KEY = 'mm_utm';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

/** Well-known campaign URLs for each launch channel. */
export const CAMPAIGN_URLS = {
  producthunt:
    'https://marketmindofficial.com/?utm_source=producthunt&utm_medium=referral&utm_campaign=launch',
  twitter:
    'https://marketmindofficial.com/?utm_source=twitter&utm_medium=social&utm_campaign=launch',
  linkedin:
    'https://marketmindofficial.com/?utm_source=linkedin&utm_medium=social&utm_campaign=launch',
} as const;

/**
 * Parse UTM params from the current URL.
 * Returns an empty object when none are present.
 */
function parseUtmParams(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const keys: (keyof UtmParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  const result: UtmParams = {};
  for (const key of keys) {
    const value = params.get(key);
    if (value) result[key] = value;
  }
  return result;
}

/**
 * Read the last-stored UTM params from localStorage.
 * Returns null when nothing is stored or the value is unparseable.
 */
export function getStoredUtmParams(): UtmParams | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UtmParams;
  } catch {
    return null;
  }
}

/**
 * Capture UTM parameters from the URL on first render and persist them to
 * localStorage.  Already-stored params are NOT overwritten unless the current
 * URL contains a fresh set — this preserves the original acquisition source
 * across page navigations that strip the query string.
 *
 * Usage:
 *   function LandingPage() {
 *     useUtmCapture();
 *     …
 *   }
 */
export function useUtmCapture(): UtmParams | null {
  const current = parseUtmParams();
  const hasCurrent = Object.keys(current).length > 0;

  useEffect(() => {
    if (!hasCurrent) return;
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(current));
    } catch {
      // localStorage may be unavailable in private browsing; fail silently.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasCurrent) return current;
  return getStoredUtmParams();
}
